import type {
  CoachingArtifactDto,
  CoachingCohortDto,
  CoachingCompanyDto,
  CoachingHistoryEventDto,
  CoachingParticipantDto,
  CoachingProgramDto,
  CoachingResourceDto,
  CoachingSessionDto,
  CoachingUserDto
} from "../services/coaching-dtos";
import { getRoleDefinition, isCoachingRole } from "../domain/roles";
import { reportAccessFields } from "./reports/reports-helpers";
import type { TableRow } from "./product-route-contracts";

/** Friendly role label ("Company admin"), never the raw code ("company_admin"). */
function roleLabel(role: string | null | undefined): string {
  if (role && isCoachingRole(role)) return getRoleDefinition(role).label;
  return role ? role.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Member";
}

/**
 * Live DTO -> surface table-row projections.
 *
 * Each product surface keeps its existing column contract; these mappers
 * translate live read-model DTOs into those columns. Signals the live API
 * does not provide yet are labeled "no live signal" instead of being faked.
 */

const NO_LIVE_SIGNAL = "no live signal";

export function nameById(records: Array<{ id: string; name?: string }>): Map<string, string> {
  return new Map(records.filter((record) => record.name).map((record) => [record.id, record.name ?? ""]));
}

function lookupName(index: Map<string, string>, id: string | undefined, fallback: string, embeddedName?: string) {
  // Live list rows carry backend-denormalized names (company_name, ...), so a
  // hard reload never has to wait for the sibling list query before names
  // render; the cross-list index and "id N" stay as fallbacks only.
  return embeddedName || (id && index.get(id)) || (id ? `id ${id}` : fallback);
}

export function liveCompanyTableRows(
  companies: CoachingCompanyDto[],
  programs: CoachingProgramDto[],
  cohorts: CoachingCohortDto[]
): TableRow[] {
  return companies.map((company) => ({
    id: company.id,
    company: company.name,
    status: company.status ?? "active",
    linkedPrograms: `${programs.filter((program) => program.company_id === company.id).length} programs`,
    linkedCohorts: `${cohorts.filter((cohort) => cohort.company_id === company.id).length} cohorts`,
    lifecycle: "live record"
  }));
}

export function liveProgramTableRows(
  programs: CoachingProgramDto[],
  companies: CoachingCompanyDto[],
  users: CoachingUserDto[]
): TableRow[] {
  const companyNames = nameById(companies);
  const userNames = nameById(users.map((user) => ({ id: user.id, name: user.name ?? user.email })));
  return programs.map((program) => ({
    id: program.id,
    program: program.name,
    company: lookupName(companyNames, program.company_id, "unassigned", program.company_name),
    coach: lookupName(userNames, program.coach_id, "unassigned", program.coach_name),
    status: program.status ?? "active",
    lifecycle: "live record"
  }));
}

export function liveCohortTableRows(
  cohorts: CoachingCohortDto[],
  companies: CoachingCompanyDto[],
  programs: CoachingProgramDto[],
  participants: CoachingParticipantDto[]
): TableRow[] {
  const companyNames = nameById(companies);
  const programNames = nameById(programs);
  return cohorts.map((cohort) => ({
    id: cohort.id,
    cohort: cohort.name,
    company: lookupName(companyNames, cohort.company_id, "unassigned", cohort.company_name),
    program: lookupName(programNames, cohort.program_id, "unassigned", cohort.program_name),
    coach: cohort.coach_name ?? (cohort.coach_id ? `id ${cohort.coach_id}` : NO_LIVE_SIGNAL),
    participants: `${participants.filter((participant) => participant.cohort_id === cohort.id).length} linked`,
    scope: "service-scoped",
    status: cohort.status ?? "active"
  }));
}

export function liveLearnerCohortTableRows(cohorts: CoachingCohortDto[]): TableRow[] {
  return cohorts.map((cohort) => ({
    id: cohort.id,
    cohort: cohort.name,
    progress: cohort.status ?? NO_LIVE_SIGNAL,
    nextSession: NO_LIVE_SIGNAL,
    reports: NO_LIVE_SIGNAL,
    peers: "peer names only"
  }));
}

export function liveParticipantTableRows(
  participants: CoachingParticipantDto[],
  cohorts: CoachingCohortDto[]
): TableRow[] {
  const cohortNames = nameById(cohorts);
  return participants.map((participant) => ({
    id: participant.id,
    participant: participant.name ?? participant.email ?? `id ${participant.id}`,
    email: participant.email ?? NO_LIVE_SIGNAL,
    cohort: lookupName(cohortNames, participant.cohort_id, "unassigned", participant.cohort_name),
    progress: participant.status ?? NO_LIVE_SIGNAL,
    privacy: "visible in scope"
  }));
}

export function liveSessionTableRows(
  sessions: CoachingSessionDto[],
  cohorts: CoachingCohortDto[],
  companies: CoachingCompanyDto[]
): TableRow[] {
  const cohortNames = nameById(cohorts);
  const companyNames = nameById(companies);
  const cohortCompany = new Map(cohorts.map((cohort) => [cohort.id, cohort.company_id]));
  return sessions.map((session) => ({
    id: session.id,
    session: session.title ?? `Session ${session.id}`,
    cohort: lookupName(cohortNames, session.cohort_id, "unassigned", session.cohort_name),
    company: lookupName(
      companyNames,
      session.company_id ?? (session.cohort_id ? cohortCompany.get(session.cohort_id) : undefined),
      "unassigned"
    ),
    startsAt: session.starts_at ?? NO_LIVE_SIGNAL,
    invite: session.invite_status ?? "not requested",
    calendar: NO_LIVE_SIGNAL,
    participantStatus: session.participant_count != null ? `${session.participant_count} learners` : NO_LIVE_SIGNAL,
    cancellation: "none recorded",
    provider: "live",
    status: session.status ?? "scheduled"
  }));
}

export function liveCohortSessionTableRows(sessions: CoachingSessionDto[]): TableRow[] {
  return sessions.map((session) => ({
    id: session.id,
    session: session.title ?? `Session ${session.id}`,
    date: session.starts_at ?? NO_LIVE_SIGNAL,
    status: session.status ?? "scheduled",
    output: NO_LIVE_SIGNAL
  }));
}

export function liveCohortParticipantTableRows(participants: CoachingParticipantDto[]): TableRow[] {
  return participants.map((participant) => ({
    id: participant.id,
    learner: participant.name ?? participant.email ?? `id ${participant.id}`,
    email: participant.email ?? NO_LIVE_SIGNAL,
    progress: participant.status ?? NO_LIVE_SIGNAL,
    invite: participant.status === "invited" ? "invited" : NO_LIVE_SIGNAL,
    privacy: "visible in scope",
    report: NO_LIVE_SIGNAL,
    transcript: NO_LIVE_SIGNAL
  }));
}

export function liveUserTableRows(users: CoachingUserDto[], companies: CoachingCompanyDto[]): TableRow[] {
  const companyNames = nameById(companies);
  return users.map((user) => ({
    id: user.id,
    name: user.name || (user.email ?? `id ${user.id}`),
    email: user.email ?? NO_LIVE_SIGNAL,
    role: roleLabel(user.role),
    status: user.membershipStatus ?? user.status ?? "active",
    scope: user.scope_label ?? lookupName(companyNames, user.company_id, "organization scope"),
    readiness: (user.membershipStatus ?? user.status) === "active" ? "active in org" : (user.membershipStatus ?? user.status ?? NO_LIVE_SIGNAL)
  }));
}

export function liveHistoryTableRows(events: CoachingHistoryEventDto[]): TableRow[] {
  return events.map((event) => ({
    id: event.id,
    action: event.action ?? "event",
    actor: event.actor_id ? `actor ${event.actor_id}` : "system",
    summary: event.summary ?? NO_LIVE_SIGNAL,
    sensitiveValues: "redacted at source",
    auditState: "append-only",
    occurredAt: event.occurred_at ?? NO_LIVE_SIGNAL
  }));
}

export function liveResourceTableRows(resources: CoachingResourceDto[]): TableRow[] {
  return resources.map((resource) => ({
    id: resource.id,
    title: resource.title,
    type: extraString(resource, "resourceType") ?? "file",
    access: "global read-only",
    status: resource.status ?? "published"
  }));
}

export function liveReportTableRows(reports: CoachingArtifactDto[], sessions: CoachingSessionDto[]): TableRow[] {
  const sessionNames = nameById(sessions.map((session) => ({ id: session.id, name: session.title })));
  return reports.map((report) => {
    const access = reportAccessFields(report);
    const row = report as CoachingArtifactDto & Record<string, unknown>;
    const sessionId = report.session_id ?? (row.sessionId as string | undefined);
    return {
      id: report.id,
      sessionId,
      report: report.summary ?? report.title ?? (row.summary as string | undefined) ?? (row.title as string | undefined) ?? `Report ${report.id}`,
      session:
        report.session_title ??
        (row.sessionTitle as string | undefined) ??
        lookupName(sessionNames, sessionId, "unlinked"),
      cohort: report.cohort_name ?? (row.cohortName as string | undefined) ?? NO_LIVE_SIGNAL,
      status: report.status ?? "ready",
      // Carry the report taxonomy/visibility so the org surfaces can render the
      // type/audience badge and the D-3 row filter can drop confidential rows.
      ...access,
      approvedAt: report.approved_at ?? (row.approvedAt as string | null | undefined) ?? null,
      reviewedAt: report.reviewed_at ?? (row.reviewedAt as string | null | undefined) ?? null
    };
  });
}

function extraString(record: object, key: string): string | undefined {
  const value = (record as Record<string, unknown>)[key];
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number") return String(value);
  return undefined;
}

export function liveRecordIdFromPath(routePath: string, segment: string): string | null {
  const parts = routePath.split("?")[0].split("/").filter(Boolean);
  const index = parts.indexOf(segment);
  const recordId = index >= 0 ? parts[index + 1] : undefined;
  return recordId && recordId !== "new" ? recordId : null;
}
